import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Modal,
  IconButton,
  CircularProgress,
} from "@mui/material";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import { format } from "date-fns";
import { useParams } from "react-router-dom";
import { getNewsletterByOrgId } from "../apis/apiList/newsletterApi";
import { downloadFileFromBox } from "../apis/apiList/boxAssetsApi";
import { useApp } from "../context/AppContext";

const ITEMS_PER_PAGE = 10;

export default function OrgNewsletterListPage() {
  const { orgId } = useParams();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [newsletters, setNewsletters] = useState([]);
  const [selectedNewsletter, setSelectedNewsletter] = useState(null);
  const { userDetails } = useApp();
  const [orgLoading, setOrgLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    async function fetchNewsletters() {
      try {
        setOrgLoading(true);
        const res = await getNewsletterByOrgId(orgId, {
          headers: { "x-access-token": userDetails.jwtToken },
        });
        setNewsletters(res?.newsletters || []);
      } catch (err) {
        console.error("Failed to fetch newsletters:", err);
      } finally {
        setOrgLoading(false);
      }
    }
    fetchNewsletters();
  }, [orgId]);

  const filteredNewsletters = newsletters.filter((n) =>
    n.subject.toLowerCase().includes(search.toLowerCase())
  );

  const paginatedNewsletters = filteredNewsletters.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  const handleViewNewsletter = (newsletter) => {
    setSelectedNewsletter(newsletter);
  };

  const handleCloseModal = () => {
    setSelectedNewsletter(null);
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      const file = await downloadFileFromBox(attachment?.boxFileId);
      if (file?.data) {
        const link = document.createElement('a');
        link.href = file?.data;
        link.download = '';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        console.error('File URL not found');
      }
    } catch (error) {
      console.error("Failed to download attachment:", error);
    }
  };

  return (
    <Box>
      <Box sx={{ borderRadius: 2, mb: 1, display: "flex", flexDirection: "column", gap: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ background: "gainsboro", borderRadius: 2 }}>
          <Typography variant="h3" sx={{ fontWeight: "bold", color: "#222", mb: 2.5 }}>
            Newsletters
          </Typography>
        </Box>

        <Box
          display="flex"
          alignItems="center"
          gap={2}
          sx={{ background: "#f4f6f8", p: 2, borderRadius: 2, pl: 0 }}
        >
          <Typography variant="body1" fontWeight="medium">
            Newsletter Email: <strong>newsletters+{orgId}@snapshotvideos.com</strong>
          </Typography>
          <IconButton onClick={() => {
                navigator.clipboard.writeText(`newsletters+${orgId}@snapshotvideos.com`)
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            }}>
            <ContentCopyIcon />
            {copied && (
                <Typography variant="body2" color="success.main">
                    Copied!
                </Typography>
            )}
          </IconButton>
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", mb: 3 }}>
          <TextField
            size="small"
            label="Search by Subject"
            value={search}
            sx={{ minWidth: 220, width: { xs: "100%", md: "400px" } }}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Box>

        {orgLoading ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
        ) : paginatedNewsletters.length > 0 ? (
          <>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#495967" }}>
                    <TableCell sx={{ color: "#fff" }}>Subject</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Received At</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Attachments</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedNewsletters.map((newsletter) => (
                    <TableRow key={newsletter._id}>
                      <TableCell>{newsletter.subject}</TableCell>
                      <TableCell>{format(new Date(newsletter.receivedAt), "PPP p")}</TableCell>
                      <TableCell>{newsletter.attachments?.length || 0} file(s)</TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleViewNewsletter(newsletter)}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box display="flex" justifyContent="center" mt={2}>
              <Pagination
                count={Math.ceil(filteredNewsletters.length / ITEMS_PER_PAGE)}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          </>
        ) : (
          <Box
            mt={6}
            textAlign="center"
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="40vh"
            color="text.secondary"
          >
            <img
              src="/images/other/newsletter.png"
              alt="Newsletters Not Found"
              style={{ width: 300, height: "auto", marginBottom: 16, opacity: 0.6 }}
            />
            <Typography variant="h2" fontWeight="bold" mb={1} color="#666666">
              Newsletters Not Found
            </Typography>
          </Box>
        )}

        <Modal open={!!selectedNewsletter} onClose={handleCloseModal}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "90%", md: "70%", lg: "60%" },
              maxHeight: "90vh",
              overflowY: "auto",
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              borderRadius: 3,
            }}
          >
            <IconButton
              onClick={handleCloseModal}
              sx={{ position: "absolute", top: 8, right: 8 }}
            >
              <CloseIcon />
            </IconButton>
            {selectedNewsletter && (
              <>
                <Typography variant="h5" fontWeight="bold" mb={1}>
                  Subject: {selectedNewsletter.subject}
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  <strong>Received:</strong> {format(new Date(selectedNewsletter.receivedAt), "PPP p")}
                </Typography>

                <Box
                  sx={{
                    bgcolor: "#f5f5f5",
                    borderRadius: 2,
                    p: 2,
                    mb: 3,
                    maxHeight: "300px",
                    overflowY: "auto",
                  }}
                >
                  <Typography variant="body1" sx={{ whiteSpace: "pre-line" }}>
                    {selectedNewsletter.body?.text || "No content available."}
                  </Typography>
                </Box>

                {selectedNewsletter.attachments?.length > 0 && (
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium" mb={1}>
                      Attachments
                    </Typography>
                    <Box display="flex" flexDirection="column" gap={1}>
                      {selectedNewsletter.attachments.map((att, idx) => (
                        <Button
                          key={idx}
                          href={att.boxFileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="outlined"
                          size="small"
                          sx={{ textTransform: "none", justifyContent: "flex-start" }}
                          onClick={() => handleDownloadAttachment(att)}
                        >
                          📎 {att.filename}
                        </Button>
                      ))}
                    </Box>
                  </Box>
                )}
              </>
            )}
          </Box>
        </Modal>
      </Box>
    </Box>
  );
}
